import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getDatabase, ref, get } from 'firebase/database';
import Preloader from '../Preloader';
import StudentStats from './StudentStats';
import StudentSessionList from './StudentSessionList';
import StudentGrades from './StudentGrades';

const ClassDetails = ({ selectedClass, closeClass, user }) => {
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState({});
  const [sessions, setSessions] = useState([]);
  const [classDetails, setClassDetails] = useState(null);
  const [accessPermissions, setAccessPermissions] = useState({});
  const [activeTab, setActiveTab] = useState('stats');

  const fetchClassDetails = async () => {
    setLoading(true);
    try {
      // Fetch class details
      const classDocRef = doc(db, 'classes', selectedClass.id);
      const classDocSnap = await getDoc(classDocRef);
      if (classDocSnap.exists()) {
        const classData = classDocSnap.data();
        setClassDetails(classData);
        setAccessPermissions({
          statsaccess: classData.statsaccess,
          gradeaccess: classData.gradeaccess,
          sessionaccess: classData.sessionaccess,
        });
      }

      // Fetch student's grades
      const gradesDocRef = doc(db, 'grades', selectedClass.id);
      const gradesDocSnap = await getDoc(gradesDocRef);
      if (gradesDocSnap.exists()) {
        const studentGrades = gradesDocSnap.data().students?.[user.uid] || {};
        setGrades(studentGrades);
      } else {
        setGrades({});
      }

      // Fetch sessions
      const dbRealtime = getDatabase();
      const sessionsRef = ref(dbRealtime, `sessions/${selectedClass.id}`);
      const sessionsSnapshot = await get(sessionsRef);
      if (sessionsSnapshot.exists()) {
        const sessionsData = sessionsSnapshot.val();
        setSessions(
          Object.entries(sessionsData).map(([id, data]) => ({ id, ...data }))
        );
      } else {
        setSessions([]);
      }
    } catch (error) {
      console.error('Error fetching class details:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClassDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return <Preloader />;
  }

  // Destructure access permissions
  const { statsaccess, gradeaccess, sessionaccess } = accessPermissions;

  // When no access is enabled, show a simple message with a back button
  if (!statsaccess && !gradeaccess && !sessionaccess) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <button
          onClick={closeClass}
          className="mb-4 px-4 py-2 bg-[var(--primary-color)] text-white rounded-md hover:bg-[var(--hover-color)] transition-colors"
        >
          &larr; Back to Classes
        </button>
        <h2 className="text-2xl font-semibold text-[var(--text-color)]">
          {classDetails?.class_name || 'Class Name'}
        </h2>
        <p className="mt-2 text-[var(--muted-color)]">
          Professor has not enabled access for this class.
        </p>
      </div>
    );
  }

  return (
    <div
      className="w-full max-w-none min-w-0 mt-4 px-3 sm:px-4 py-4 rounded"
      style={{ backgroundColor: 'var(--section-bg)', color: 'var(--text-color)' }}
    >
      {/* Header */}
      <div className="flex justify-between items-center gap-3 mb-4 min-w-0">
        <h2 className="text-2xl font-bold" style={{ color: 'var(--text-color)' }}>
          {classDetails?.class_name || 'Class Name'}
        </h2>
        <button
          onClick={closeClass}
          className="font-bold py-2 px-4 rounded transition-all duration-200"
          style={{ backgroundColor: 'var(--primary-color)', color: 'var(--nav-active-text)' }}
          onMouseOver={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--hover-color)')
          }
          onMouseOut={(e) =>
            (e.currentTarget.style.backgroundColor = 'var(--primary-color)')
          }
        >
          &times;
        </button>
      </div>
  
      {/* Navigation Tabs */}
      <ul
        className="ce-class-tabs grid grid-cols-3 w-full min-w-0 border-b mb-3"
        role="tablist"
        style={{ borderColor: 'var(--muted-color)' }}
      >
        {statsaccess && (
          <li className="min-w-0">
            <button
              onClick={() => setActiveTab('stats')}
              className="w-full cursor-pointer py-2 px-3 sm:px-4 text-center transition-colors duration-200"
              style={
                activeTab === 'stats'
                  ? {
                      color: 'var(--primary-color)',
                      borderBottom: '2px solid var(--primary-color)',
                    }
                  : { color: 'var(--muted-color)' }
              }
              onMouseOver={(e) => {
                if (activeTab !== 'stats') {
                  e.currentTarget.style.color = 'var(--text-color)';
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== 'stats') {
                  e.currentTarget.style.color = 'var(--muted-color)';
                }
              }}
            >
              Stats
            </button>
          </li>
        )}
        {gradeaccess && (
          <li className="min-w-0">
            <button
              onClick={() => setActiveTab('grades')}
              className="w-full cursor-pointer py-2 px-3 sm:px-4 text-center transition-colors duration-200"
              style={
                activeTab === 'grades'
                  ? {
                      color: 'var(--primary-color)',
                      borderBottom: '2px solid var(--primary-color)',
                    }
                  : { color: 'var(--muted-color)' }
              }
              onMouseOver={(e) => {
                if (activeTab !== 'grades') {
                  e.currentTarget.style.color = 'var(--text-color)';
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== 'grades') {
                  e.currentTarget.style.color = 'var(--muted-color)';
                }
              }}
            >
              Grades
            </button>
          </li>
        )}
        {sessionaccess && (
          <li className="min-w-0">
            <button
              onClick={() => setActiveTab('sessions')}
              className="w-full cursor-pointer py-2 px-3 sm:px-4 text-center transition-colors duration-200"
              style={
                activeTab === 'sessions'
                  ? {
                      color: 'var(--primary-color)',
                      borderBottom: '2px solid var(--primary-color)',
                    }
                  : { color: 'var(--muted-color)' }
              }
              onMouseOver={(e) => {
                if (activeTab !== 'sessions') {
                  e.currentTarget.style.color = 'var(--text-color)';
                }
              }}
              onMouseOut={(e) => {
                if (activeTab !== 'sessions') {
                  e.currentTarget.style.color = 'var(--muted-color)';
                }
              }}
            >
              Sessions
            </button>
          </li>
        )}
      </ul>
  
      {/* Tab Content */}
      <div className="mt-3 w-full min-w-0">
        {activeTab === 'stats' && statsaccess && (
          <div className="block w-full min-w-0" role="tabpanel">
            <StudentStats
              grades={grades}
              sessions={sessions}
              classId={selectedClass.id}
              user={user}
            />
          </div>
        )}
        {activeTab === 'grades' && gradeaccess && (
          <div className="block w-full min-w-0" role="tabpanel">
            <StudentGrades
              grades={grades}
              sessions={sessions}
              classId={selectedClass.id}
              user={user}
            />
          </div>
        )}
        {activeTab === 'sessions' && sessionaccess && (
          <div className="block w-full min-w-0" role="tabpanel">
            <StudentSessionList
              sessions={sessions}
              classId={selectedClass.id}
              user={user}
              grades={grades}
            />
          </div>
        )}
      </div>
    </div>
  );  
};

export default ClassDetails;
